import { useState } from "react";
import { api, notifyError } from "@/lib/api";
import { Calculator } from "@phosphor-icons/react";
import { Field, ResultPanel } from "./shared";

const TILES = [
  { key: "relief_rate", label: "Relief rate", pct: true, color: "#FFB020" },
  { key: "gross_credit", label: "Gross credit", money: true },
  { key: "claimable_credit", label: "Claimable now", money: true, color: "#00A86B" },
  { key: "net_investment_cost", label: "Net cost", money: true },
  { key: "unused_relief", label: "Unused relief", money: true, color: "#E63946" },
  { key: "holding_period_end", label: "Hold shares until" },
];

export default function EIICalc({ onSaved }) {
  const [f, setF] = useState({ investment_amount: "", tax_liability: "", tax_year: new Date().getFullYear(), share_issue_date: "", spouse_investment_amount: "", spouse_tax_liability: "" });
  const [calc, setCalc] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        investment_amount: Number(f.investment_amount),
        tax_liability: Number(f.tax_liability),
        tax_year: Number(f.tax_year),
        share_issue_date: f.share_issue_date,
        spouse_investment_amount: Number(f.spouse_investment_amount || 0),
        spouse_tax_liability: Number(f.spouse_tax_liability || 0),
      };
      const r = await api.post("/tax/calculate/eii", payload);
      setCalc(r.data);
      onSaved && onSaved();
    } catch (err) { notifyError(err); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={run} className="glass rounded-2xl p-6 space-y-4">
        <div>
          <div className="eyebrow mb-1">Employment & Investment Incentive</div>
          <p className="text-white/55 text-sm">Income-tax relief for investing in qualifying Irish SMEs.</p>
        </div>
        <Field label="Investment amount (€)" type="number" step="0.01" required data-testid="eii-investment" value={f.investment_amount} onChange={(e) => set("investment_amount", e.target.value)} />
        <Field label="Your income-tax liability this year (€)" type="number" step="0.01" required data-testid="eii-liability" value={f.tax_liability} onChange={(e) => set("tax_liability", e.target.value)} hint="Relief is capped at your tax liability." />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Tax year" type="number" data-testid="eii-year" value={f.tax_year} onChange={(e) => set("tax_year", e.target.value)} />
          <Field label="Share issue date" type="date" data-testid="eii-issue-date" value={f.share_issue_date} onChange={(e) => set("share_issue_date", e.target.value)} />
        </div>
        <details className="text-sm text-white/60">
          <summary className="cursor-pointer">Joint assessment (optional)</summary>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <Field label="Spouse investment (€)" type="number" step="0.01" data-testid="eii-spouse-investment" value={f.spouse_investment_amount} onChange={(e) => set("spouse_investment_amount", e.target.value)} />
            <Field label="Spouse tax liability (€)" type="number" step="0.01" data-testid="eii-spouse-liability" value={f.spouse_tax_liability} onChange={(e) => set("spouse_tax_liability", e.target.value)} />
          </div>
        </details>
        <button type="submit" disabled={busy} className="btn-gold w-full justify-center" data-testid="eii-calculate"><Calculator size={16} weight="fill" /> {busy ? "Calculating…" : "Calculate EII relief"}</button>
      </form>
      {calc ? <ResultPanel calc={calc} tiles={TILES} /> : (
        <div className="glass rounded-2xl p-6 flex items-center justify-center text-white/40 text-sm text-center">Enter your investment details to estimate EII relief.</div>
      )}
    </div>
  );
}
